import { useRef, useState } from "react";
import { http } from "@/lib/api";

const MIME = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus", "audio/aac"];
const pickMime = () => (window.MediaRecorder && MediaRecorder.isTypeSupported ? MIME.find((m) => MediaRecorder.isTypeSupported(m)) : "") || "";
const EXT = { webm: "webm", mp4: "m4a", ogg: "ogg", aac: "aac" };

export const micError = (e) => {
  if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) return "This browser can't record audio. Use Chrome, Edge or Safari 14.5+.";
  if (e?.name === "NotAllowedError" || e?.name === "SecurityError") return "Microphone blocked. Allow mic access in the address bar (or open JARVIS in its own tab) and try again.";
  if (e?.name === "NotFoundError") return "No microphone found on this device.";
  if (e?.name === "NotReadableError") return "Microphone is busy in another app.";
  return `Microphone error: ${e?.message || e}`;
};

export function useRecorder() {
  const [recording, setRecording] = useState(false);
  const [level, setLevel] = useState(0);
  const rec = useRef(null);
  const chunks = useRef([]);
  const vad = useRef(null);

  const stopVad = () => {
    if (!vad.current) return;
    cancelAnimationFrame(vad.current.raf);
    clearTimeout(vad.current.max);
    vad.current.ctx.close().catch(() => {});
    vad.current = null;
    setLevel(0);
  };

  const start = async (onAutoStop) => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
    const mime = pickMime();
    const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
    chunks.current = [];
    mr.ondataavailable = (e) => e.data.size && chunks.current.push(e.data);
    mr.start(250);
    rec.current = mr;
    setRecording(true);
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx || !onAutoStop) return;
    const ctx = new Ctx();
    const an = ctx.createAnalyser();
    an.fftSize = 1024;
    ctx.createMediaStreamSource(stream).connect(an);
    const buf = new Uint8Array(an.fftSize);
    let spoke = false, quietSince = 0;
    const started = performance.now();
    const tick = () => {
      an.getByteTimeDomainData(buf);
      let sum = 0;
      for (const v of buf) sum += (v - 128) ** 2;
      const rms = Math.sqrt(sum / buf.length) / 128;
      setLevel(Math.min(1, rms * 6));
      const now = performance.now();
      if (rms > 0.04) { spoke = true; quietSince = 0; }
      else if (spoke) quietSince ||= now;
      if ((spoke && quietSince && now - quietSince > 1400) || (!spoke && now - started > 8000)) { stopVad(); onAutoStop(spoke); return; }
      vad.current.raf = requestAnimationFrame(tick);
    };
    vad.current = { ctx, raf: requestAnimationFrame(tick), max: setTimeout(() => { stopVad(); onAutoStop(true); }, 30000) };
  };

  const stop = () =>
    new Promise((resolve, reject) => {
      stopVad();
      const mr = rec.current;
      if (!mr || mr.state === "inactive") return resolve("");
      mr.onstop = async () => {
        mr.stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        const type = (mr.mimeType || "audio/webm").split(";")[0];
        const blob = new Blob(chunks.current, { type });
        if (blob.size < 1200) return resolve("");
        const form = new FormData();
        form.append("file", blob, `voice.${EXT[type.split("/")[1]] || "webm"}`);
        try { resolve((await http.post("/voice/transcribe", form)).data.text); } catch (e) { reject(e); }
      };
      mr.stop();
    });

  return { recording, level, start, stop };
}

let current = null;
export async function speak(text) {
  const { data } = await http.post("/voice/speak", { text });
  current?.pause();
  current = new Audio(`data:audio/mp3;base64,${data.audio}`);
  await new Promise((resolve) => {
    current.onended = resolve;
    current.onerror = resolve;
    current.play().catch(resolve);
  });
}

export const stopSpeaking = () => current?.pause();
